import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const processSteps = [
  { num: '01', title: 'Discovery', desc: 'We learn about your business, competition, existing tech, and growth objectives.' },
  { num: '02', title: 'Audit', desc: 'We evaluate your website, search presence, advertising, software, CRM, and lead-handling.' },
  { num: '03', title: 'Strategy', desc: 'We identify opportunities, define priorities, and create a practical plan based on goals.' },
  { num: '04', title: 'Build', desc: 'Our team creates the websites, campaigns, software, integrations, and automation.' },
  { num: '05', title: 'Launch', desc: 'We test the customer journey, confirm tracking, launch work, and monitor performance.' },
  { num: '06', title: 'Optimize', desc: 'We review results, identify weak points, and continually improve the entire ecosystem.' }
];

const fitPoints = [
  "You are tired of coordinating multiple agencies and vendors.",
  "Your website, marketing, and internal systems do not work together.",
  "You generate leads but struggle to manage or convert them.",
  "Your current agency reports activity without explaining business results.",
  "You need both strategic guidance and hands-on implementation.",
  "Your website or software no longer supports how your company operates."
];

const ProcessAndFit = () => {
  return (
    <section id="process" className="section-padding relative bg-gray-950">
      <div className="container-custom">
        
        {/* Process */}
        <div className="mb-32">
          <div className="text-center max-w-3xl mx-auto mb-20 relative z-10">
            <span className="text-purple-400 font-medium tracking-widest text-base uppercase mb-4 block font-display">
              How We Work
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6">
              A Clearer Way to Improve Your <span className="text-gradient-alt">Digital Business.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, idx) => (
              <motion.div 
                key={step.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl relative overflow-hidden group"
              >
                <div className="text-8xl font-display font-black text-white/[0.03] absolute -top-4 -right-4 group-hover:text-cyan-500/[0.05] transition-colors duration-500">
                  {step.num}
                </div>
                <div className="text-cyan-400 font-display font-bold text-lg mb-2 relative z-10">Step {step.num}</div>
                <h3 className="text-2xl font-display font-bold text-white mb-4 relative z-10">{step.title}</h3>
                <p className="text-gray-400 font-light relative z-10 leading-relaxed text-base">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Who We Help */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-50px" }}
          className="max-w-5xl mx-auto bg-gradient-to-br from-cyan-900/20 to-purple-900/20 border border-purple-500/30 rounded-[3rem] p-8 md:p-16 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          
          <div className="text-center mb-12 relative z-10">
            <span className="text-cyan-400 font-medium tracking-widest text-base uppercase mb-4 block font-display">
              Built For Real Partners
            </span>
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white">
              Is LAJ Digital the Right Fit?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-6 relative z-10">
            {fitPoints.map((point, idx) => (
              <div key={idx} className="flex items-start">
                <div className="mt-1 mr-4 w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 border border-purple-500/50">
                  <SafeIcon icon="FiCheck" className="text-purple-400 text-base" />
                </div>
                <p className="text-gray-300 font-light text-base">{point}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessAndFit;